import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Save, Upload, MapPin, Phone, Mail, Building2, 
  Loader2, CheckCircle, Image as ImageIcon
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { venueApi } from '../../lib/api';
import { resolveAssetUrl } from '../../config';
import { toast } from 'sonner';
import VenueHeader from '../../components/venue/VenueHeader';

const VenueSettings = () => {
  const { user, loading: authLoading } = useAuth();
  const [venuePartner, setVenuePartner] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    venue_name: '',
    venue_type: '',
    phone: '',
    email: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: ''
  });

  useEffect(() => {
    // Access is enforced by <RequireVenue> in App.js.
    if (!authLoading) fetchVenueData();
  }, [user, authLoading]);

  const fetchVenueData = async () => {
    try {
      if (!user?.venue_partner_id) {
        setLoading(false);
        return;
      }

      const vpResponse = await venueApi.get(
        `/venue/partner/${user.venue_partner_id}`
      );
      
      const venueData = vpResponse.data;
      setVenuePartner(venueData);

      if (venueData.hub_location_id) {
        const locationResponse = await venueApi.get(
          `/locations/${venueData.hub_location_id}`
        );
        setLocation(locationResponse.data);
        
        setFormData({
          venue_name: venueData.venue_name || '',
          venue_type: venueData.venue_type || '',
          phone: locationResponse.data.phone || venueData.phone || '',
          email: locationResponse.data.email || venueData.email || '',
          street_address: locationResponse.data.address || venueData.street_address || '',
          city: locationResponse.data.city || venueData.city || '',
          state: locationResponse.data.state || venueData.state || '',
          zip_code: locationResponse.data.zip_code || venueData.zip_code || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch venue data:', error);
      toast.error('Failed to load venue settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      
      await venueApi.put(
        `/venue/partner/${user.venue_partner_id}`,
        {
          venue_name: formData.venue_name,
          venue_type: formData.venue_type,
          phone: formData.phone,
          email: formData.email,
          street_address: formData.street_address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code
        }
      );
      
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      await venueApi.post(
        `/venue/partner/${user.venue_partner_id}/logo`,
        uploadData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Logo uploaded successfully!');
      fetchVenueData();
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // V5 Loading State
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="text-center">
          <img src="/tappd-logo.png" alt="Tappd" className="h-12 mx-auto mb-6" />
          <Loader2 className="animate-spin mx-auto mb-4" size={48} style={{ color: '#14b8a6' }} />
          <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      {/* V5 Venue Header with Mobile Nav */}
      <VenueHeader user={user} venuePartner={venuePartner} locationData={location} />

      {/* Content */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 pt-24">
        {/* V5 Page Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <p className="font-mono text-xs mb-2" style={{ color: '#14b8a6' }}>
              // venue_settings
            </p>
            <h1 
              className="text-[32px] mb-1"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
            >
              Location Settings
            </h1>
            <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
              Manage your venue information
            </p>
          </div>
          
          {/* V5 Save Button - Teal for Venue */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 text-[14px] font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
            style={{ 
              background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(20, 184, 166, 0.4)'
            }}
          >
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        <div className="space-y-6">
          {/* V5 Logo Section */}
          <div 
            className="bg-white p-6"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #14b8a6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <h3 
              className="text-[18px] font-semibold mb-4"
              style={{ color: '#1e1b4b' }}
            >
              Venue Logo
            </h3>
            <div className="flex items-center gap-6">
              <div 
                className="w-32 h-32 flex items-center justify-center overflow-hidden"
                style={{ 
                  backgroundColor: 'rgba(20, 184, 166, 0.05)',
                  border: '2px dashed rgba(20, 184, 166, 0.3)',
                  borderRadius: '12px'
                }}
              >
                {location?.logo_url ? (
                  <img
                    src={resolveAssetUrl(location.logo_url)}
                    alt="Venue Logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <ImageIcon size={48} style={{ color: 'rgba(30, 27, 75, 0.2)' }} />
                )}
              </div>
              <div>
                <p className="text-[14px] mb-4" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                  Upload your venue logo. Recommended size: 512x512px. Max 5MB.
                </p>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <button
                    type="button"
                    disabled={uploadingLogo}
                    onClick={() => document.getElementById('logo-upload').click()}
                    className="flex items-center gap-2 px-4 py-2 text-[14px] font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    style={{ 
                      backgroundColor: 'white',
                      border: '2px solid rgba(20, 184, 166, 0.3)',
                      borderRadius: '12px',
                      color: '#14b8a6'
                    }}
                  >
                    {uploadingLogo ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <Upload size={16} />
                    )}
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </button>
                </label>
              </div>
            </div>
          </div>

          {/* V5 Basic Info Card */}
          <div 
            className="bg-white p-6"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #14b8a6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <h3 
              className="text-[18px] font-semibold mb-4"
              style={{ color: '#1e1b4b' }}
            >
              Basic Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label 
                  className="block text-[14px] font-medium mb-2"
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  Venue Name
                </label>
                <div className="relative">
                  <Building2 
                    size={18} 
                    className="absolute left-4 top-1/2 -translate-y-1/2" 
                    style={{ color: 'rgba(30, 27, 75, 0.3)' }} 
                  />
                  <input
                    type="text"
                    name="venue_name"
                    value={formData.venue_name}
                    onChange={handleInputChange}
                    placeholder="Your Venue Name"
                    className="w-full pl-11 pr-4 transition-all"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#14b8a6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
              <div>
                <label 
                  className="block text-[14px] font-medium mb-2"
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  Venue Type
                </label>
                <select
                  name="venue_type"
                  value={formData.venue_type}
                  onChange={handleInputChange}
                  className="w-full px-4 bg-white cursor-pointer transition-all"
                  style={{ 
                    height: '48px',
                    border: '2px solid rgba(30, 27, 75, 0.1)',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#14b8a6';
                    e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">Select type...</option>
                  <option value="Bar">Bar</option>
                  <option value="Restaurant">Restaurant</option>
                  <option value="Bar/Restaurant">Bar/Restaurant</option>
                  <option value="Brewery">Brewery</option>
                  <option value="Winery">Winery</option>
                  <option value="Coffee Shop">Coffee Shop</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* V5 Contact Info Card */}
          <div 
            className="bg-white p-6"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #f97316',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <h3 
              className="text-[18px] font-semibold mb-4"
              style={{ color: '#1e1b4b' }}
            >
              Contact Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label 
                  className="block text-[14px] font-medium mb-2"
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  Phone Number
                </label>
                <div className="relative">
                  <Phone 
                    size={18} 
                    className="absolute left-4 top-1/2 -translate-y-1/2" 
                    style={{ color: 'rgba(30, 27, 75, 0.3)' }} 
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 555-5555"
                    className="w-full pl-11 pr-4 transition-all"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#14b8a6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
              <div>
                <label 
                  className="block text-[14px] font-medium mb-2"
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  Email
                </label>
                <div className="relative">
                  <Mail 
                    size={18} 
                    className="absolute left-4 top-1/2 -translate-y-1/2" 
                    style={{ color: 'rgba(30, 27, 75, 0.3)' }} 
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="venue@example.com"
                    className="w-full pl-11 pr-4 transition-all"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#14b8a6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* V5 Address Card */}
          <div 
            className="bg-white p-6"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #8b5cf6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <h3 
              className="text-[18px] font-semibold mb-4"
              style={{ color: '#1e1b4b' }}
            >
              Address
            </h3>
            <div className="space-y-4">
              <div>
                <label 
                  className="block text-[14px] font-medium mb-2"
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  Street Address
                </label>
                <div className="relative">
                  <MapPin 
                    size={18} 
                    className="absolute left-4 top-1/2 -translate-y-1/2" 
                    style={{ color: 'rgba(30, 27, 75, 0.3)' }} 
                  />
                  <input
                    type="text"
                    name="street_address"
                    value={formData.street_address}
                    onChange={handleInputChange}
                    placeholder="123 Main Street"
                    className="w-full pl-11 pr-4 transition-all"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#14b8a6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label 
                    className="block text-[14px] font-medium mb-2"
                    style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                  >
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    className="w-full px-4 transition-all"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#14b8a6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <div>
                  <label 
                    className="block text-[14px] font-medium mb-2"
                    style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                  >
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="State"
                    className="w-full px-4 transition-all"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#14b8a6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <div>
                  <label 
                    className="block text-[14px] font-medium mb-2"
                    style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                  >
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                    placeholder="12345"
                    className="w-full px-4 transition-all"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#14b8a6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* V5 Account Status Card */}
          <div 
            className="bg-white p-6"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #22c55e',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <h3 
              className="text-[18px] font-semibold mb-4"
              style={{ color: '#1e1b4b' }}
            >
              Account Status
            </h3>
            <div 
              className="flex items-center gap-4 p-4"
              style={{ 
                backgroundColor: 'rgba(34, 197, 94, 0.05)',
                border: '1px solid rgba(34, 197, 94, 0.1)',
                borderRadius: '12px'
              }}
            >
              <div 
                className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: venuePartner?.status === 'approved' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.12)', borderRadius: '12px' }}
              >
                <CheckCircle size={24} style={{ color: venuePartner?.status === 'approved' ? '#22c55e' : '#f59e0b' }} />
              </div>
              <div>
                <p className="text-[14px] font-semibold" style={{ color: venuePartner?.status === 'approved' ? '#22c55e' : '#b45309' }}>
                  {venuePartner?.status === 'approved' ? 'Approved venue partner' : venuePartner?.status === 'rejected' ? 'Application not approved' : 'Pending approval'}
                </p>
                <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                  {venuePartner?.status === 'approved' ? 'Your venue is active and receiving coasters' : venuePartner?.status === 'rejected' ? 'Contact hello@tappd.app if you have questions' : 'We review applications within a few business days'}
                </p>
              </div>
            </div>
          </div>

          {/* Save again at the foot of the form (the header button is a long
              scroll away on a phone) */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 text-[14px] font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(20, 184, 166, 0.4)'
              }}
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueSettings;
